"use client";

import {
  getReviewLessonQuizApiV1AdminReviewLessonItemUuidQuizGet,
  queueReviewRemedialGenerationApiV1AdminReviewLessonItemUuidGenerateRemedialPost,
} from "@/src/api/sdk.gen";
import { formatApiErrorMessage } from "@/src/lib/error-utils";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Sparkles } from "lucide-react";

type QuizQuestion = { concepts?: unknown; question_text?: string };

function toConceptList(questions: QuizQuestion[]): string[] {
  const set = new Set<string>();
  for (const q of questions) {
    const c = q.concepts;
    if (Array.isArray(c)) {
      for (const v of c) {
        const s = String(v ?? "").trim();
        if (s) set.add(s);
      }
    } else if (typeof c === "string") {
      const s = c.trim();
      if (s) set.add(s);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export interface GenerateRemedialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemUuid: string;
  onQueued: () => void;
  onToast?: (message: string, type: "success" | "error") => void;
}

export default function GenerateRemedialDialog({
  isOpen,
  onClose,
  itemUuid,
  onQueued,
  onToast,
}: GenerateRemedialDialogProps) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  const concepts = useMemo(() => toConceptList(questions), [questions]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSelected([]);
    setError(null);
    const load = async () => {
      setLoading(true);
      try {
        const { data: resData, error: reqErr } = await getReviewLessonQuizApiV1AdminReviewLessonItemUuidQuizGet({
          path: { item_uuid: itemUuid },
        });
        const data = (resData || {}) as any;
        if (reqErr || !data?.hasQuiz) {
          if (!cancelled) setError(data?.message ?? "Quiz must be generated first.");
          return;
        }
        if (!cancelled) setQuestions(Array.isArray(data.questions) ? data.questions : []);
      } catch {
        if (!cancelled) setError("Failed to load quiz concepts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, itemUuid]);

  const handleClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  const toggleConcept = (c: string) => {
    setSelected((prev) => (prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c]));
  };

  const handleSubmit = async () => {
    if (selected.length === 0) {
      setError("Pick at least one struggled concept.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data: resData, error: reqErr } = await queueReviewRemedialGenerationApiV1AdminReviewLessonItemUuidGenerateRemedialPost({
        path: { item_uuid: itemUuid },
        body: { struggledConcepts: selected },
      });
      const data = (resData || {}) as any;
      if (reqErr) {
        const msg = formatApiErrorMessage(reqErr, "Failed to queue remedial generation");
        setError(msg);
        onToast?.(msg, "error");
        return;
      }
      onToast?.(data.message ?? "Remedial generation queued", "success");
      onQueued();
      onClose();
    } catch (e) {
      const msg = formatApiErrorMessage(e, "Request failed");
      setError(msg);
      onToast?.(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-xl bg-card border border-border shadow-xl p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <Dialog.Title className="text-lg font-semibold text-foreground">
                    Generate remedial lesson
                  </Dialog.Title>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Pick struggled concepts from this lesson quiz.
                </p>

                {loading ? (
                  <p className="text-sm text-muted-foreground py-3">Loading quiz concepts…</p>
                ) : concepts.length === 0 ? (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    No quiz concepts found for this lesson.
                  </p>
                ) : (
                  <div className="max-h-72 overflow-auto border border-border rounded-lg p-3 space-y-2">
                    {concepts.map((c) => (
                      <label key={c} className="flex items-start gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={selected.includes(c)}
                          onChange={() => toggleConcept(c)}
                          className="mt-0.5"
                        />
                        <span>{c}</span>
                      </label>
                    ))}
                  </div>
                )}

                {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSubmit()}
                    disabled={submitting || loading || selected.length === 0 || concepts.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {submitting ? "Queueing…" : "Generate remedial"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

